import { Box, Button, Card, CardContent, Chip, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import CustomCircularProgress from "./CustomCircularProgress";
import { useResearchAreas } from "../hooks/useResearch";
import { uploadedFileUrl } from "../utils/uploadedFileUrl";

// The progress material is uploaded through the admin panel as a PDF, which the
// button below opens in a new tab.
const progressUrl = (progressLink: string | null) =>
  progressLink ? uploadedFileUrl("pelgroupResearchProgress", progressLink) : "";

const progressButtonSx = {
  borderColor: "#C46A2D",
  color: "#8A4319",
  borderRadius: "8px",
  textTransform: "none",
  fontFamily: "Source Sans Pro SemiBold",
  "&:hover": {
    borderColor: "#9E4E1F",
    backgroundColor: "rgba(196,106,45,0.08)",
  },
  "&.Mui-disabled": {
    borderColor: "#E4DED2",
    color: "#9A948A",
  },
};

export default function OngoingResearch() {
  const { data, isLoading, isError } = useResearchAreas();

  if (isLoading) return <CustomCircularProgress color="#C46A2D" />;

  if (isError || !data)
    return (
      <Typography sx={{ color: "#62605A", textAlign: "center" }}>
        Research areas are unavailable right now.
      </Typography>
    );

  return (
    <Box>
      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Typography
          sx={{
            color: "#C46A2D",
            fontFamily: "Source Sans Pro SemiBold",
            fontSize: 13,
            letterSpacing: "1.8px",
            textTransform: "uppercase",
            mb: 1,
          }}
        >
          Ongoing Research
        </Typography>
        <Typography
          component="h2"
          sx={{
            color: "#20242A",
            fontFamily: "Source Sans Pro SemiBold",
            fontSize: { xs: 28, md: 36 },
            lineHeight: 1.12,
            mb: 1.5,
          }}
        >
          Research areas
        </Typography>
        <Typography sx={{ color: "#62605A", fontSize: { xs: 15, md: 17 }, lineHeight: 1.75, maxWidth: 920 }}>
          The group’s current work is organized around five research directions in power electronics,
          electrical drives, high-frequency conversion, wide-bandgap technologies, and energy storage.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {data.map((area, index) => (
          <Grid
            item
            xs={12}
            md={index === data.length - 1 && data.length % 2 === 1 ? 12 : 6}
            key={area.id}
          >
            <Card
              sx={{
                height: "100%",
                display: "flex",
                minWidth: 0,
                flexDirection: "column",
                border: "1px solid #E4DED2",
                borderRadius: "10px",
                overflow: "hidden",
                backgroundColor: "#FFFFFF",
                boxShadow: "0 14px 34px rgba(32,36,42,0.07)",
                transition: "border-color .2s ease, box-shadow .2s ease, transform .2s ease",
                "&:hover": {
                  borderColor: index % 2 === 0 ? "#9BC7C3" : "#D7B38D",
                  boxShadow: "0 18px 40px rgba(32,36,42,0.12)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: { xs: 220, sm: 250, md: 280 },
                  minWidth: 0,
                  maxWidth: "100%",
                  overflow: "hidden",
                  backgroundColor: "#F1EDE5",
                  flex: "0 0 auto",
                  position: "relative",
                }}
              >
                <Box
                  component="img"
                  src={uploadedFileUrl("pelgroupResearch", area.image)}
                  alt={area.title}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "block",
                    width: "100%",
                    height: "100%",
                    maxWidth: "100%",
                    objectFit: "cover",
                    objectPosition: "center",
                    filter: "saturate(94%) contrast(96%)",
                  }}
                />
              </Box>

              <CardContent
                sx={{
                  p: { xs: 2.5, md: 3.2 },
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={`Area ${index + 1}`}
                    sx={{
                      height: 24,
                      bgcolor: index % 2 === 0 ? "rgba(46,125,120,0.10)" : "rgba(196,106,45,0.10)",
                      color: index % 2 === 0 ? "#1F4F4B" : "#8A4319",
                      fontWeight: 700,
                    }}
                  />
                </Stack>

                <Typography
                  component="h3"
                  sx={{
                    color: "#20242A",
                    fontFamily: "Source Sans Pro SemiBold",
                    fontSize: { xs: 20, md: 23 },
                    lineHeight: 1.2,
                    mb: 1.5,
                  }}
                >
                  {area.title}
                </Typography>

                <Typography sx={{ color: "#62605A", fontSize: { xs: 14.5, md: 15.5 }, lineHeight: 1.7, mb: 2.5 }}>
                  {area.text}
                </Typography>

                <Box sx={{ mt: "auto" }}>
                  {progressUrl(area.progressLink) ? (
                    <Button
                      component="a"
                      href={progressUrl(area.progressLink)}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                      sx={progressButtonSx}
                    >
                      View Progress
                    </Button>
                  ) : (
                    <Button disabled variant="outlined" sx={progressButtonSx}>
                      Progress material N/A
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
